import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Buton, DurumRozeti, Ikon, SatirDugmesi, SatirMetni } from "@koclukpanel/ui";
import { ApiHatasi, eksikHaftalikRaporlar, type EksikHaftalikRapor } from "./api";

/**
 * EKSİK HAFTALIK VELİ RAPORLARI — F-REP-07a (A4, Emre onayı 05.10.2026)
 *
 * "Uzman VE yönetim ekranında" (F-REP-07). Koç kendi kalemlerini iş
 * listesinde de görüyor; bu liste Raporlar ekranının öğrenci seçilmemiş
 * hâlinde, kapsamındaki bütün eksikleri (hafta — öğrenci) bir arada
 * gösteriyor. Satıra basınca o öğrencinin raporları, o hafta önceden
 * dolu "Yeni rapor" formuyla açılır.
 *
 * Kural sunucuda (`rapor/haftalik-rapor.ts`); ekran hiçbir şey hesaplamıyor.
 */
export function EksikVeliRaporlari({ onSec }: { onSec: (satir: EksikHaftalikRapor) => void }) {
  const [acik, setAcik] = useState(false);
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["eksik-haftalik-raporlar"],
    queryFn: eksikHaftalikRaporlar,
  });

  if (isPending) return null;
  if (isError) {
    return (
      <p role="alert" className="text-hata-500 text-sm">
        {error instanceof ApiHatasi ? error.message : "Eksik raporlar alınamadı."}
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <p className="text-onay-700 text-sm" role="status">
        Son 4 haftada eksik haftalık veli raporu yok.
      </p>
    );
  }

  // En eski gecikme: kartın tek cümlelik özeti ("en eskisi 4 hafta gecikti").
  const enEski = Math.max(...data.map((s) => s.gecikmeHafta));
  const kritik = data.filter((s) => s.kritik).length;

  /*
    ================================================================
    40 SATIRLIK LİSTE DEĞİL, SAYAÇ KARTI — sürüm 9a iç düzen turu L1
    ================================================================
    Ölçüldü: liste sayfanın en üstünde 40 satır tutuyordu ve ekran 3500 px'i aşıyordu; asıl iş
    (öğrenci seçip raporuna gitmek) ekranın altına itiliyordu. UNİKOÇ'ta eksik raporlar küçük bir
    sayaç kartında duruyor. Kart sayıyı ve en eski gecikmeyi söylüyor; liste düğmeyle açılıyor.
    Hiçbir kalem kaybolmuyor: açılınca liste eskisi gibi, satıra basınca o hafta önceden dolu
    "Yeni rapor" formu açılıyor.
  */
  return (
    <section
      aria-label="Eksik veli raporları"
      className="bg-notr-0 border-mor-kenar flex flex-col gap-3 rounded-[1.5rem] border p-4 shadow-(--golge-unk-kart) md:p-6"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className="bg-eksik-zemin text-eksik-nokta grid size-11 shrink-0 place-items-center rounded-xl"
        >
          <Ikon ad="rapor" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          {/* UNİKOÇ sayaç rozeti (tarama-1 §2.8): sorun varsa kırmızı dolu kare-yuvarlak. */}
          <h3 className="text-metin-koyu flex items-center gap-2 text-base font-bold">
            Eksik veli raporları
            <span
              aria-label={`${data.length} eksik`}
              className="bg-eksik-nokta text-notr-0 inline-grid min-w-6 place-items-center rounded-lg px-1.5 text-xs font-bold"
            >
              {data.length}
            </span>
          </h3>
          <p className="text-metin-ikincil text-sm">
            {kritik > 0 ? `${kritik} rapor kritik gecikmede; ` : ""}
            en eskisi {enEski === 1 ? "geçen hafta" : `${enEski} hafta önce`} bitti.
          </p>
        </div>
        <Buton
          gorunum="kesikli"
          aria-expanded={acik}
          aria-controls="eksik-veli-listesi"
          onClick={() => setAcik((a) => !a)}
        >
          {acik ? "Listeyi gizle" : `Listeyi göster (${data.length})`}
        </Buton>
      </div>
      <p className="text-notr-600 text-xs">
        Programı yayınlanmış ve biten her hafta için haftalık rapor beklenir (Pazartesi–Pazar).
        Kontrole gönderilen rapor listeden düşer.
      </p>
      {acik && (
        <ul id="eksik-veli-listesi" className="flex max-h-[28rem] flex-col gap-1 overflow-y-auto">
          {data.map((s) => (
            <li key={`${s.ogrenciId}-${s.haftaBasi}`}>
              <SatirDugmesi onClick={() => onSec(s)}>
                <SatirMetni
                  baslik={`${s.hafta} — ${s.ogrenciAdi}`}
                  altBaslik={s.kocAdi ? `Koç: ${s.kocAdi}` : "Koç atanmamış"}
                />
                <DurumRozeti durum={s.kritik ? "eksik" : "bekliyor"}>
                  {s.gecikmeHafta === 1 ? "Geçen hafta" : `${s.gecikmeHafta} hafta gecikti`}
                </DurumRozeti>
              </SatirDugmesi>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
